<template>
  <div class="flex flex-col min-h-screen items-center">
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="flex">
        <PopupUserMenu :user="user" :active-item="'oauth-management'" />
        <LogoIcon />
      </div>
      <DropdownMenu />
    </div>
    <div class="flex flex-grow max-w-[1280px] mt-[60px] mx-auto w-full">
      <UserMenu :user="user" :active-item="'oauth-management'" />
      <div class="flex-1 px-6">
        <div class="flex justify-between items-center mb-6">
          <h1 class="text-2xl font-semibold">{{ $t('messages.menu.oauth_management') }}</h1>
          <button
            class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            @click="showCreateDialog = true"
          >
            + New Application
          </button>
        </div>

        <div v-if="loading" class="text-gray-500">Loading...</div>
        <div v-else-if="error" class="text-red-500">{{ error }}</div>
        <div v-else>
          <table class="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Portal Name</th>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Status</th>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Creator</th>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Created Date</th>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Revoker</th>
                <th class="text-left px-4 py-3 font-semibold text-sm text-gray-700">Revoked At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="app in applications" :key="app.id" class="border-t border-gray-200 hover:bg-gray-50">
                <td class="px-4 py-3">
                  <router-link
                    :to="{ name: 'OAuthApplicationDetailPage', params: { id: app.id } }"
                    class="text-blue-600 hover:underline"
                  >
                    {{ app.name }}
                  </router-link>
                </td>
                <td class="px-4 py-3">
                  <span
                    :class="app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="px-2 py-1 rounded-full text-xs font-semibold uppercase"
                  >
                    {{ app.status }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ app.creator_email || '—' }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ formatDate(app.created_at) }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ app.revoker_email || '—' }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ app.revoked_at ? formatDate(app.revoked_at) : '—' }}</td>
              </tr>
              <tr v-if="applications.length === 0">
                <td colspan="6" class="px-4 py-8 text-center text-gray-500">No applications found.</td>
              </tr>
            </tbody>
          </table>

          <div class="flex justify-between items-center mt-4 text-sm text-gray-600">
            <span>Total: {{ meta.total }} applications</span>
            <div class="flex gap-2">
              <button
                :disabled="meta.page <= 1"
                class="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                @click="fetchApplications(meta.page - 1)"
              >
                Prev
              </button>
              <span class="px-3 py-1">{{ meta.page }} / {{ meta.total_pages }}</span>
              <button
                :disabled="meta.page >= meta.total_pages"
                class="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                @click="fetchApplications(meta.page + 1)"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <OAuthApplicationForm
          v-if="showCreateDialog"
          mode="create"
          @close="showCreateDialog = false"
          @saved="onApplicationCreated"
        />

        <!-- One-time secret display after create -->
        <div v-if="createdSecret" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div class="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
            <h2 class="text-xl font-semibold mb-2">Application Created</h2>
            <p class="text-sm text-orange-600 mb-4">⚠️ The client secret is shown only once. Copy it now — it cannot be retrieved later.</p>
            <div class="mb-4">
              <p class="block text-xs font-medium text-gray-500 mb-1">Client ID</p>
              <div class="bg-gray-100 rounded-lg p-3 font-mono text-sm break-all">{{ createdSecret.uid }}</div>
            </div>
            <div class="mb-6">
              <p class="block text-xs font-medium text-gray-500 mb-1">Client Secret</p>
              <div class="bg-gray-100 rounded-lg p-3 font-mono text-sm break-all">{{ createdSecret.secret }}</div>
            </div>
            <button
              class="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              @click="createdSecret = null"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import { formatDateWithTimezone } from '@/utils/dateUtils';
import LogoIcon from '@/components/LogoIcon.vue';
import DropdownMenu from '@/components/DropdownMenu.vue';
import UserMenu from '@/components/UserMenu.vue';
import PopupUserMenu from '@/components/PopupUserMenu.vue';
import OAuthApplicationForm from '@/components/OAuthApplicationForm.vue';
import axios from 'axios';

export default {
  name: 'OAuthManagementPage',
  components: { PopupUserMenu, LogoIcon, DropdownMenu, UserMenu, OAuthApplicationForm },
  data() {
    return {
      applications: [],
      meta: { total: 0, page: 1, per_page: 20, total_pages: 1 },
      loading: false,
      error: null,
      showCreateDialog: false,
      createdSecret: null,
    };
  },
  computed: {
    user() {
      return useAuthStore().current_user || {};
    },
  },
  mounted() {
    this.fetchApplications(1);
  },
  methods: {
    async fetchApplications(page = 1) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/oauth-management/applications', { params: { page, per_page: 20 } });
        this.applications = data.data;
        this.meta = data.meta;
      } catch (e) {
        this.error = e.response?.data?.detail || 'Failed to load applications.';
      } finally {
        this.loading = false;
      }
    },
    formatDate: formatDateWithTimezone,
    onApplicationCreated(data) {
      this.showCreateDialog = false;
      this.createdSecret = { uid: data.uid, secret: data.secret };
      this.fetchApplications(1);
    },
  },
};
</script>
